'use client'

import { storyblokEditable } from '@storyblok/react/rsc'
import { type FC, type SubmitEventHandler, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { Card, CardContent } from 'base-ui'

import { type StoryblokComponentProps } from '../../model'
import { type StoryblokBmiCalculatorType } from '../../storyblok'

import { StoryblokBmiCalculatorCard } from './StoryblokBmiCalculatorCard'
import { StoryblokBmiCalculatorCategoryTable } from './StoryblokBmiCalculatorCategoryTable'
import { StoryblokBmiCalculatorForm } from './StoryblokBmiCalculatorForm'
import { StoryblokBmiCalculatorResult } from './StoryblokBmiCalculatorResult'

const getNumber = (name: string, form: HTMLFormElement): number => {
  const field = form.elements.namedItem(name)

  if (!(field instanceof HTMLInputElement)) {
    return Number.NaN
  }

  return field.valueAsNumber
}

/**
 * Body mass index (BMI) calculator for Storyblok. Related Wiki page https://en.wikipedia.org/wiki/Body_mass_index.
 */
export const StoryblokBmiCalculator: FC<
  StoryblokComponentProps<StoryblokBmiCalculatorType>
> = ({ blok }) => {
  const [value, setValue] = useState<number>()

  const onSubmit: SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()

    const height = getNumber('height', event.target)
    const weight = getNumber('weight', event.target)

    if (height > 0 && weight > 0) {
      const exponent = 2

      setValue(weight / (height / 100) ** exponent)
    } else {
      setValue(undefined)
    }
  }

  return (
    <div
      {...storyblokEditable(blok)}
      className="grid grid-cols-1 gap-6 desktop:grid-cols-3"
    >
      <StoryblokBmiCalculatorCard
        title={<FormattedMessage id="bmi.calculator.formTitle" />}
      >
        <StoryblokBmiCalculatorForm onSubmit={onSubmit} />
      </StoryblokBmiCalculatorCard>
      <StoryblokBmiCalculatorCard
        title={<FormattedMessage id="bmi.calculator.resultTitle" />}
      >
        <Card className="grid flex-1 place-items-center bg-light-primary-medium">
          <CardContent className="px-content-l py-content-2xl text-center">
            <StoryblokBmiCalculatorResult value={value} />
          </CardContent>
        </Card>
      </StoryblokBmiCalculatorCard>
      <StoryblokBmiCalculatorCard
        title={<FormattedMessage id="bmi.calculator.categoryTitle" />}
      >
        <StoryblokBmiCalculatorCategoryTable />
      </StoryblokBmiCalculatorCard>
    </div>
  )
}
